import React, { useEffect, useState } from 'react';
import Button from '@salesforce/design-system-react/module/components/button';
import Dropdown from '@salesforce/design-system-react/module/components/menu-dropdown';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import Icon from '@salesforce/design-system-react/module/components/icon';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import debounce from 'lodash.debounce';
import { PageHeaderControl } from '@salesforce/design-system-react';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  exchangeTransactionsSelector,
  getExchangeTransactions,
  createNotes,
  updateNotes,
} from '../../store/exchanges/transactionsSlice';
import { getDateTimeFromTimestamp, getTimeIntervalText } from '../../utilities/datetime';
import { useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { exchangePageOptions } from '../../constants/defiAccount';
import { isEmpty } from '@firebase/util';
import CashSvg from '../../assets/cash.svg';
import { getNumberWithCommas } from '../../utilities/money';
import { getSyncTime } from '../../services/snapshot';
import WalletAddressEllipsis from '../../components/shared/WalletAddressEllipsis';
import ProviderLogo from '../../components/shared/ProviderLogo';
import Tooltip from '@salesforce/design-system-react/components/tooltip';

const CustomDataTableTransactionId = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
    <Link to={`/exchanges/transaction/${item._id}`}>
      <WalletAddressEllipsis text={children.replace(/<[^>]*>?/gm, '')} maxTextCount={20} />
    </Link>
  </DataTableCell>
  );
};
CustomDataTableTransactionId.displayName = DataTableCell.displayName;

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(children) : ''}</p>
    </DataTableCell>
  );
};
CustomDataTableCell.displayName = DataTableCell.displayName;

const CustomDataTableName = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={`${children}`}>
      <>
        <ProviderLogo
          size={{ width: 18, height: 18 }}
          provider={item?.account_provider}
          color={item?.account_color}
        />
        <Link to={`/exchanges/${item.account}/transactions`}>{children}</Link>
      </>
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

const CustomDataTableNotes = ({ children, ...props }) => {
  const [editedValue, setEditedValue] = useState(children ? children.note : '');
  const dispatch = useDispatch();
  const syncData = getSyncTime();

  const addUpdateNotesHandler = (values) => {
    dispatch(values.id ? updateNotes(values) : createNotes(values));
  };

  const onCellEditData = (item, data) => {
    // setEditedValue(item);
    if (item === children.note) return;
    props.setShowToast(true);
    const values = {
      note: {
        note: item,
        entity_id: data.item._id,
        type: 'transaction',
      },
      id: children ? children.id : null,
    };
    addUpdateNotesHandler(values);
  };

  return (
    <DataTableCell
      title={editedValue ? editedValue : 'Add note'}
      {...props}
    >
      <IconSettings iconPath="/assets/icons">
        <Input
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search',
              }}
              name="edit"
              category="utility"
            />
          }
          type="text"
          value={editedValue}
          placeholder="Add note"
          onChange={(e) => {
            setEditedValue(e.target.value);
          }}
          className={`editable-cell-input tablenote`}
          onBlur={(e) => onCellEditData(e.target.value, props)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCellEditData(e.target.value, props);
            }
          }}
          styleInput={{border: '0px'}}
        />
      </IconSettings>
    </DataTableCell>
  );
};
CustomDataTableNotes.displayName = DataTableCell.displayName;

const CustomDataTableCellOne = ({ children, ...props }) => {
  const amount = getNumberWithCommas(children || 0, 4);
  const { item } = props;
  const currency = item?.currency || '';
  return (
    <DataTableCell title={children} {...props}>
      <span className="slds-m-right_xx-small">
        <img src={CashSvg} alt=""></img>
      </span>
      {amount} {currency}
    </DataTableCell>
  );
};
CustomDataTableCellOne.displayName = DataTableCell.displayName;

const CustomDataTableChain = ({ children, ...props }) => {
  return (
    <DataTableCell title={children} {...props}>
      <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
        <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        {children === 'deposit' ? 'Buy' : 'Sell'}
      </div>
    </DataTableCell>
  );
};
CustomDataTableChain.displayName = DataTableCell.displayName;

const ExchangeTransactions = () => {
  const dispatch = useDispatch();
  const { transactions, loading, pagination, errors, success, message } = useSelector(
    exchangeTransactionsSelector
  );
  const [sortColumn, setSortColumn] = useState('opportunityName');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);
  const navigate = useNavigate();
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [text, setText] = useState('Recently updated');
  const [searchText, setSearchText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [rows, setRowData] = React.useState('');
  const [query, setQuery] = React.useState('');

  useEffect(() => {
    dispatch(getExchangeTransactions());
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (transactions.length > 0) {
      setItems(transactions);
    }

    setAPICalledTime(new Date());
    const data = getTimeIntervalText(APICalledTime);
    setText(data);
  }, [transactions, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = getTimeIntervalText(APICalledTime);
      setText(data);
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(getExchangeTransactions({ sort_column: property, sort_order: sortDirection }));

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getExchangeTransactions({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  const refreshTable = () => {
    setHasMore(true);
    setPaginated(0);
    setItems([]);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getExchangeTransactions());
    }
  };

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getExchangeTransactions({
          q: values,
        })
      );
      values === '' && getExchangeTransactions();
    }, 1000),
    []
  );

  // const searchHandler = (value) => {
  //   setHasMore(false);
  //   // setItems([]);
  //   // setPaginated(0);
  //   setQuery(value);
  //   const results = items.filter((row) => {
  //     const regex = new RegExp(value, "gi");
  //     return row.txid.match(regex);
  //   });
  //   setRowData(results);

  // };
  // const items = rows.length === 0 && !query ? items : rows;

  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <div className="">
            <Input
              iconLeft={
                <InputIcon
                  assistiveText={{
                    icon: 'Search',
                  }}
                  name="search"
                  category="utility"
                />
              }
              id="search_text"
              placeholder="Search Transaction"
              value={searchText}
              onChange={(e) => onSearchText(e.target.value)}
              fixedTextRight={
                <Tooltip
                  id="search-tooltip"
                  align="top right"
                  content="Search works on the basis of transaction id and type"
                  position='overflowBoundaryElement'
                />
              }
            />
          </div>
        </IconSettings>
      </PageHeaderControl>
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  if (success && showToast) {
    toast.success(message, {
      id: 'exchange-transaction-success',
    });
    setShowToast(false);
  }

  if (!isEmpty(errors) && showToast) {
    toast.error(errors, {
      id: 'exchange-transaction-error',
    });
    setShowToast(false);
  }

  return (
    <div className="pageContent">
      <Toaster position="top-right" reverseOrder={false} />
      <PageHeader
        icon={<Icon assistiveText={{ label: 'Accounts' }} category="standard" name="account" />}
        info={`${items && items.length} items • ${text}`}
        joined
        label="Exchange Transactions"
        title="All Transactions"
        nameSwitcherDropdown={
          <Dropdown
            assistiveText={{ icon: 'Name Switcher' }}
            buttonClassName="slds-button_icon-small"
            buttonVariant="icon"
            iconCategory="utility"
            iconName="down"
            onSelect={(e) => {
              navigate('/exchanges/' + e.value);
            }}
            id="page-header-name-switcher-dropdown"
            options={exchangePageOptions}
          />
        }
        // onRenderActions={() =>
        //   Actions({
        //     onAddAction: handleAccountAdd,
        //     onDropDownSelect: (value) => {
        //       setAccountsDropDown(value);
        //     },
        //     value: accountsDropDown,
        //     btnLabel: 'Create Rule',
        //     options: [
        //       { label: 'Choose', value: '0' },
        //       { label: 'Menu Item One', value: 'A0' },
        //       { label: 'Menu Item Two', value: 'B0' },
        //       { label: 'Menu Item Three', value: 'C0' },
        //       { type: 'divider' },
        //       { label: 'Menu Item Four', value: 'D0' },
        //     ],
        //   })
        // }
        onRenderControls={controls}
        truncate
        variant="object-home"
      />

      <div
        style={{
          height: `${transactions.length === 0 ? '100%' : 'calc(100vh - 220px)'}`,
        }}
      >
        <DataTable
          assistiveText={{
            actionsHeader: 'actions',
            columnSort: 'sort this column',
            columnSortedAscending: 'asc',
            columnSortedDescending: 'desc',
            selectAllRows: 'Select all rows',
            selectRow: 'Select this row',
          }}
          fixedHeader
          fixedLayout
          items={items}
          id="DataTableExample-FixedHeaders"
          onSort={handleSort}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          noRowHover
        >
          <DataTableColumn
            isSorted={sortColumn === 'txid'}
            label="Transaction ID"
            primaryColumn
            property="txid"
            sortable
            sortDirection={sortColumnDirection.txid}
            width="12rem"
          >
            <CustomDataTableTransactionId />
          </DataTableColumn>
          <DataTableColumn label="Account" property="account_name" width="12rem">
            {/* <CustomDataTableCellOne /> */}
            <CustomDataTableName />
          </DataTableColumn>
          {/* <DataTableColumn label="Stage" property="stage" /> */}

          <DataTableColumn label="Type" property="type" width="10rem">
            <CustomDataTableChain />
          </DataTableColumn>
          <DataTableColumn sortable isDefaultSortDescending label="Leverage" property="amount">
            <CustomDataTableCellOne />
          </DataTableColumn>
          <DataTableColumn sortable isDefaultSortDescending label="Amount" property="amount">
            <CustomDataTableCellOne />
          </DataTableColumn>
          <DataTableColumn label="Notes" property="note">
            <CustomDataTableNotes setShowToast={setShowToast} />
          </DataTableColumn>
          <DataTableColumn label="Transaction Date" sortable property="createdAt">
            <CustomDataTableCell />
          </DataTableColumn>
        </DataTable>
        {(transactions.length === 0 || items.length === 0) && !hasMore && (
          <div className="nodatafound">
            <div>
              <img src="/assets/empty-state/empty-folder.png" alt="" style={{ height: '150px' }} />
              <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                Data not available
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExchangeTransactions;
